import { useState, useEffect, useCallback, useRef } from "react";

export interface UseApiResult<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

// Runs an async fetcher, tracking loading/error state and supporting refetch.
export function useApi<T>(fetcher: () => Promise<T>): UseApiResult<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const requestIdRef = useRef(0);

  const refetch = useCallback(async (): Promise<void> => {
    const requestId = ++requestIdRef.current;
    setLoading(true);
    setError(null);

    try {
      const result = await fetcher();
      if (requestId === requestIdRef.current) setData(result);
    } catch (err: unknown) {
      if (requestId === requestIdRef.current) {
        setError(err instanceof Error ? err.message : "An error occurred");
      }
    } finally {
      if (requestId === requestIdRef.current) setLoading(false);
    }
  }, [fetcher]);

  useEffect(() => {
    void refetch();

    return () => {
      requestIdRef.current += 1;
    };
  }, [refetch]);

  return { data, loading, error, refetch };
}
